// src/components/avatar/MyAvatar.js
//
// The signed-in user's own avatar (avatar-maker look) wearing their shop
// outfit. Live — it changes the moment they save in the avatar maker or wear
// a shop item.
//
//   <MyAvatar size={72} />
//   <MyAvatar size={112} fullSize={210} toggle />   — with a Portrait /
//        Full body switch; the choice is remembered on this device
//
// useAvatarView() / <AvatarViewSwitch /> are the same switch for pages that
// draw the avatar themselves (the avatar maker).

import React, { useEffect, useState } from "react";
import AvatarWithOutfit from "./AvatarWithOutfit";
import { useMyAvatar } from "../../services/avatarService";
import { useMyCloset } from "../../services/shopService";

const KEY = "sbl-avatar-view";
const listeners = new Set();
const readView = () => {
  try {
    return localStorage.getItem(KEY) === "full" ? "full" : "portrait";
  } catch {
    return "portrait";
  }
};
let current = null;

/** ["portrait" | "full", setView] — shared by every switch on the page. */
export const useAvatarView = () => {
  if (current === null) current = readView();
  const [view, setLocal] = useState(current);
  useEffect(() => {
    listeners.add(setLocal);
    return () => listeners.delete(setLocal);
  }, []);
  const setView = (v) => {
    current = v;
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* private mode */
    }
    listeners.forEach((f) => f(v));
  };
  return [view, setView];
};

export const AvatarViewSwitch = ({ className = "" }) => {
  const [view, setView] = useAvatarView();
  return (
    <span className={`sbl-av-switch ${className}`} role="group" aria-label="How to show my avatar">
      <button type="button" className={view === "portrait" ? "is-on" : ""} aria-pressed={view === "portrait"} onClick={() => setView("portrait")}>
        🙂 Portrait
      </button>
      <button type="button" className={view === "full" ? "is-on" : ""} aria-pressed={view === "full"} onClick={() => setView("full")}>
        🧍 Full body
      </button>
    </span>
  );
};

const MyAvatar = ({ size = 64, fullSize, toggle = false, className = "", title }) => {
  const me = useMyAvatar();
  const closet = useMyCloset();
  const [view] = useAvatarView();
  const full = toggle && view === "full";
  return (
    <AvatarWithOutfit look={me.look} outfit={closet.outfit} size={full ? fullSize || Math.round(size * 1.8) : size} full={full} className={className} title={title} />
  );
};

export default MyAvatar;
